import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
// 若無此檔案，可將邏輯直接寫入組件
import { ensureNamespaceAsync } from '../../i18n/ensurePageI18n';

// ================= 圖片導入區 =================
import machineFull from './img/machine_full-1.png';
import detailBrush from './img/machine_full-2.png';
import detailSuction from './img/machine_full-3.png';

import img4 from './img/machine_full-1-1.png';
import img5 from './img/machine_full-1-2.png';
import img6 from './img/machine_full-1-3.png';

// LOGO 圖片
import wscanLogo from './img/wscanLogo.png';

// 臨時佔位圖
import placeholderImg from './img/zh.jpg';

const bundles = {
  en: {
    title_main: "Dust Removal Device",
    title_sub: "",
    description: {
      p1: "Utilizing a brush that rotates in the opposite direction to the substrate's movement, this device moves foreign particles on the substrate toward the dust collection port. Airflow attracts and collects these particles into a dust collection net.",
      p2: "Designed exclusively for substrate cleaning, it employs fine brushes and powerful suction to thoroughly clean both sides of the substrate, including—but not limited to—uneven surfaces and edges."
    },
    specs: {
      items: [
        { label: "Corresponding Substrate Width", value: "1100-1300mm" },
        { label: "Corresponding Substrate Thickness", value: "Substrate 0.04-3.2mm" },
        { label: "Brush Wire Diameter", value: "Approximately 0.1-0.15mm" },
        { label: "Brush Rotational Speed", value: "20-50rpm (60Hz)" },
        { label: "Suction Capacity", value: "6M3/min 1.2kPa (Filter: 99% filtration for particles above 5µ)" }
      ],
      right_title: "Antistatic / Brush Dust Removal / Brush Self-Cleaning",
      machine_items: [
        { label: "Dust Removal Device Size", value: "W1500xD160xH400mm" },
        { label: "Conveyor Section Speed", value: "50M/min" },
        { label: "UTILITY", value: "3ΦAC220V 15A / AC220V 1A" },
        { label: "Dust Collection Device Size", value: "W1200xD600xH850mm" },
        { label: "Weight of Each Device", value: "Main Body | 80KG\nDust Collector | 60KG" }
      ]
    }
  },
  zh: {
    title_main: "除塵裝置",
    title_sub: "Dust Removal Device",
    description: {
      p1: "利用與基板移動方向反向旋轉之毛刷，將基板上之異物往集塵口移動。利用氣流將異物吸引，收集至集塵網中。",
      p2: "本裝置專門使用在基板之清潔用，使用精細之毛刷及強力之吸力，可將基板上下兩面清潔乾淨，包括且不限於凹凸不平之表面、板邊均可清潔乾淨。"
    },
    specs: {
      items: [
        { label: "對應基板寬度", value: "1100-1300mm" },
        { label: "對應基板厚度", value: "基板0.04-3.2mm" },
        { label: "毛刷線徑", value: "約0.1-0.15mm" },
        { label: "毛刷回轉數", value: "20-50rpm(60Hz)" },
        { label: "吸塵能力", value: "6M3/min 1.2kPa\n過濾器:5µ以上99%過濾" }
      ],
      right_title: "抗靜電 / 毛刷除塵 / 毛刷自潔",
      machine_items: [
        { label: "除塵裝置尺寸", value: "W1500xD160xH400mm" },
        { label: "輸送段速度", value: "50M/min" },
        { label: "UTILITY", value: "3ΦAC220V 15A/AC220V 1A" },
        { label: "集塵裝置尺寸", value: "W1200xD600xH850mm" },
        { label: "裝置各重量", value: "本體 | 80KG\n集塵機 | 60KG" }
      ]
    }
  }
};

const safeList = (tFunc, key) => {
  const raw = tFunc(key, { returnObjects: true });
  if (Array.isArray(raw)) return raw;
  return [];
};

const ImageGroup = ({ images }) => {
  const [imgLarge, imgSmall1, imgSmall2] = [
    images[0] || placeholderImg,
    images[1] || placeholderImg,
    images[2] || placeholderImg
  ];
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
      <div className="md:col-span-2 w-full rounded-lg overflow-hidden shadow-md border border-gray-100 group">
        <div className="w-full aspect-[21/9] bg-gray-100 relative overflow-hidden">
          <img src={imgLarge} alt="Large View" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition duration-700" />
        </div>
      </div>
      <div className="w-full rounded-lg overflow-hidden shadow-md border border-gray-100 group">
        <div className="w-full aspect-[16/9] bg-gray-100 relative overflow-hidden">
          <img src={imgSmall1} alt="Detail View 1" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition duration-700" />
        </div>
      </div>
      <div className="w-full rounded-lg overflow-hidden shadow-md border border-gray-100 group">
        <div className="w-full aspect-[16/9] bg-gray-100 relative overflow-hidden">
          <img src={imgSmall2} alt="Detail View 2" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition duration-700" />
        </div>
      </div>
    </div>
  );
};

// 單獨的資料列組件，用於模擬圖片中的條紋樣式
const SpecRow = ({ label, value }) => (
  <div className="flex flex-row items-center bg-gray-100 p-3 md:p-4 rounded min-h-[60px]">
    <div className="w-5/12 md:w-1/2 font-bold text-blue-900 text-sm md:text-base">{label}</div>
    <div className="w-7/12 md:w-1/2 text-gray-800 text-sm md:text-base whitespace-pre-line leading-tight">{value}</div>
  </div>
);

const Ultrasonic = () => {
  const { t, i18n } = useTranslation('ultrasonic'); // specify namespace
  const [ready, setReady] = useState(false);
  useEffect(() => {
    let mounted = true;
    ensureNamespaceAsync(i18n, 'ultrasonic', bundles).then(() => {
      if (mounted) setReady(true);
    });
    return () => { mounted = false; };
  }, [i18n]);

  if (!ready) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black text-gray-300">Loading…</div>
    );
  }

  const currentLang = i18n.language || 'zh';
  const group1Images = [machineFull, detailBrush, detailSuction];
  const group2Images = [img4, img5, img6];
  const logoImg = wscanLogo || placeholderImg;

  return (
    <div className="min-h-screen flex flex-col items-center p-4 md:p-8 font-sans bg-gradient-to-br from-gray-950 via-gray-900 to-black text-white">
      <div className="w-full max-w-6xl relative mb-8">
        <Link to="/" className="inline-block px-5 py-2 bg-blue-600 hover:bg-blue-500 rounded-lg text-white font-medium transition shadow-lg">
          ← {currentLang.startsWith('zh') ? '返回' : 'Back'}
        </Link>
      </div>

      <div className="max-w-6xl w-full bg-white shadow-2xl rounded-xl overflow-hidden text-gray-800">

        {/* Main Content */}
        <section className="p-6 md:p-12 border-b-8 border-gray-200">
          <div className="text-left mb-8 border-b pb-4 border-gray-200">
            <h1 className="text-3xl md:text-4xl font-bold text-blue-900 mb-2">{t('ultrasonic:title_main')}</h1>
            <h2 className="text-xl text-gray-600 font-medium">{t('ultrasonic:title_sub')}</h2>
          </div>
          <ImageGroup images={group1Images} />
          <div className="bg-blue-50 p-6 md:p-8 rounded-xl border-l-4 border-blue-600 mb-10">
            <h3 className="text-xl font-bold text-blue-900 mb-4 flex items-center">
              <span className="w-2 h-8 bg-blue-600 mr-3 rounded-full"></span>
              {currentLang.startsWith('zh') ? '原理與特點' : 'Principles & Features'}
            </h3>
            <p className="text-gray-700 leading-relaxed mb-4 text-lg">{t('ultrasonic:description.p1')}</p>
            <p className="text-gray-700 leading-relaxed text-lg">{t('ultrasonic:description.p2')}</p>
          </div>
          <ImageGroup images={group2Images} />
        </section>

        {/* ================= SPECIFICATION TABLES (Modified for Height Alignment) ================= */}
        <section className="p-6 md:p-12 bg-white">
          {/* items-stretch 是關鍵，它強迫左右兩欄高度一致 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 items-stretch">

            {/* === LEFT COLUMN: Rows + Logo === */}
            <div className="flex flex-col h-full gap-3">
              {/* 1. Data Rows */}
              <div className="flex flex-col gap-3">
                {safeList(t, 'ultrasonic:specs.items').map((row, idx) => (
                  <SpecRow key={idx} label={row.label} value={row.value} />
                ))}
              </div>

              {/* 2. Logo Box (使用 flex-1 讓它自動填滿剩餘高度，確保底部對齊) */}
              <div className="flex-1 bg-blue-900 rounded flex justify-center items-center min-h-[100px] p-6 mt-2 md:mt-0">
                <img src={logoImg} alt="WSCAN Logo" />
              </div>
            </div>

            {/* === RIGHT COLUMN: Header + Rows === */}
            <div className="flex flex-col h-full gap-3">
              {/* 1. Header Box */}
              {/* 為了讓高度對稱，這個 Header 的高度建議設定為與左邊 Logo 區塊或單行 Row 類似的視覺權重 */}
              <div className="bg-blue-900 text-white font-bold p-4 rounded flex items-center justify-center text-center text-lg md:text-xl min-h-[80px]">
                {t('ultrasonic:specs.right_title')}
              </div>

              {/* 2. Data Rows */}
              <div className="flex flex-col gap-3 flex-1">
                {safeList(t, 'ultrasonic:specs.machine_items').map((row, idx) => (
                  <SpecRow key={idx} label={row.label} value={row.value} />
                ))}
              </div>
            </div>

          </div>
        </section>
      </div>

      <div className="mt-8 text-gray-400 text-xs">© 2025 Wintriss Engineering. All rights reserved.</div>
    </div>
  );
};

export default Ultrasonic;

